import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import type { FmWorkOrderDto } from '../services-work-order.models';
import { WO_OFFLINE_TTL_MS, type WoOfflineCacheEntry } from './wo-offline.models';

const KEY_PREFIX = 'wo_offline_cache_v1:';
const INDEX_KEY = 'wo_offline_cache_index_v1';

@Injectable({ providedIn: 'root' })
export class WoOfflineCacheService {
  private entryKey(organizationId: number, projectId: number, workOrderId: number): string {
    return `${KEY_PREFIX}${organizationId}:${projectId}:${workOrderId}`;
  }

  async put(organizationId: number, projectId: number, workOrder: FmWorkOrderDto): Promise<void> {
    const workOrderId = workOrder.id;
    if (workOrderId == null || !Number.isFinite(workOrderId)) {
      return;
    }
    const entry: WoOfflineCacheEntry = {
      workOrderId,
      organizationId,
      projectId,
      cachedAt: new Date().toISOString(),
      workOrder: { ...workOrder, organizationId: workOrder.organizationId ?? organizationId, projectId: workOrder.projectId ?? projectId },
    };
    await Preferences.set({ key: this.entryKey(organizationId, projectId, workOrderId), value: JSON.stringify(entry) });
    await this.addToIndex(organizationId, projectId, workOrderId);
    await this.evictStale();
  }

  async get(organizationId: number, projectId: number, workOrderId: number): Promise<FmWorkOrderDto | null> {
    const { value } = await Preferences.get({ key: this.entryKey(organizationId, projectId, workOrderId) });
    if (!value) {
      return null;
    }
    try {
      const entry = JSON.parse(value) as WoOfflineCacheEntry;
      if (this.isExpired(entry)) {
        await this.remove(organizationId, projectId, workOrderId);
        return null;
      }
      return entry.workOrder;
    } catch {
      return null;
    }
  }

  async remove(organizationId: number, projectId: number, workOrderId: number): Promise<void> {
    await Preferences.remove({ key: this.entryKey(organizationId, projectId, workOrderId) });
    await this.removeFromIndex(organizationId, projectId, workOrderId);
  }

  private isExpired(entry: WoOfflineCacheEntry): boolean {
    const t = Date.parse(entry.cachedAt);
    if (!Number.isFinite(t)) {
      return true;
    }
    if (Date.now() - t > WO_OFFLINE_TTL_MS) {
      return true;
    }
    const status = entry.workOrder.status;
    return status === 'COMPLETED' || status === 'CANCELLED';
  }

  private async readIndex(): Promise<string[]> {
    const { value } = await Preferences.get({ key: INDEX_KEY });
    if (!value) {
      return [];
    }
    try {
      const parsed = JSON.parse(value) as string[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private async writeIndex(keys: string[]): Promise<void> {
    await Preferences.set({ key: INDEX_KEY, value: JSON.stringify(keys) });
  }

  private indexToken(organizationId: number, projectId: number, workOrderId: number): string {
    return `${organizationId}:${projectId}:${workOrderId}`;
  }

  private async addToIndex(organizationId: number, projectId: number, workOrderId: number): Promise<void> {
    const token = this.indexToken(organizationId, projectId, workOrderId);
    const index = await this.readIndex();
    if (!index.includes(token)) {
      index.push(token);
      await this.writeIndex(index);
    }
  }

  private async removeFromIndex(organizationId: number, projectId: number, workOrderId: number): Promise<void> {
    const token = this.indexToken(organizationId, projectId, workOrderId);
    const index = await this.readIndex();
    const next = index.filter((k) => k !== token);
    if (next.length !== index.length) {
      await this.writeIndex(next);
    }
  }

  private async evictStale(): Promise<void> {
    const index = await this.readIndex();
    const kept: string[] = [];
    for (const token of index) {
      const [orgRaw, projectRaw, woRaw] = token.split(':');
      const organizationId = Number(orgRaw);
      const projectId = Number(projectRaw);
      const workOrderId = Number(woRaw);
      if (!Number.isFinite(organizationId) || !Number.isFinite(projectId) || !Number.isFinite(workOrderId)) {
        continue;
      }
      const { value } = await Preferences.get({ key: this.entryKey(organizationId, projectId, workOrderId) });
      if (!value) {
        continue;
      }
      try {
        const entry = JSON.parse(value) as WoOfflineCacheEntry;
        if (this.isExpired(entry)) {
          await Preferences.remove({ key: this.entryKey(organizationId, projectId, workOrderId) });
        } else {
          kept.push(token);
        }
      } catch {
        await Preferences.remove({ key: this.entryKey(organizationId, projectId, workOrderId) });
      }
    }
    await this.writeIndex(kept);
  }
}
